import { useRef } from 'react';
import type { Dispatch, SetStateAction, MutableRefObject } from 'react';
import { breakpoints, Button, Inline, Stack } from '@etg/wings';
import { ContentWrapper } from '@eti/components';
import { useProperty, useSearch, useTranslation } from '@eti/providers';
import { css, etiColors } from '@eti/styles';
import { SortAscending } from '@phosphor-icons/react';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import ResultFiltersToggleButton from '../search-filters/v3/ResultFiltersToggleButton';
import { resetAllFilters } from '../search-filters/utils/searchFilters';
import type { Filters } from '../search-filters/v3/MobileFilters';
import { Wrapper } from '../v1/SearchResults';
import ResultSearchFiltersV1 from '../v1/ResultSearchFilters';
import type { SearchFormValues } from '../../../../common/search-form-panel/SearchFormNewContainer';
import type { NearbyDatePricesType } from '../../models';
import MobileFilters from '../search-filters/v3/MobileFilters';
import { useResultPageContext } from './contexts/ResultPageContext';
import { getQuickSortOptions, getSortingDropDownOptions } from './quickSorting/utils';
import ResultsSection from './ResultsSection';
import FloatingSaveTripButton from './FloatingSaveTripButton';

const sortAndFilterControlsStyles = css`
  background: ${etiColors.white};
  padding: 8px;
  width: 100%;

  span {
    text-transform: capitalize;
  }
`;

const sortingButtonStyles = css`
  width: 100%;
`;

const filters1MobileStyles = css`
  display: inline-block;
  width: 100%;
`;

const innerWrapperStyles = (hasSelectedTrip: boolean) => css`
  @media (max-width: ${breakpoints._970}) {
    ${!hasSelectedTrip && 'padding-top: 16px'};
  }
`;

interface SearchResultsMobileProps {
  isNearbyDatesLoading: boolean;
  nearbyDatePrices: NearbyDatePricesType;
  onFilter: (filter: any) => void;
  searchFormWatchRef: MutableRefObject<UseFormWatch<SearchFormValues> | null>;
  selectedFilters: Filters;
  setFetchMoreResults: Dispatch<SetStateAction<boolean>>;
  setValueRef: MutableRefObject<UseFormSetValue<SearchFormValues> | null>;
  sidebarRef: MutableRefObject<HTMLDivElement | null>;
}

const SearchResultsMobile = ({
  isNearbyDatesLoading,
  nearbyDatePrices,
  onFilter,
  searchFormWatchRef,
  selectedFilters,
  setFetchMoreResults,
  setValueRef,
  sidebarRef,
}: SearchResultsMobileProps) => {
  const {
    closeFiltersPanel,
    isFiltersPanelOpen,
    isMobileSortDialogOpen,
    isSearchLoading,
    searchData,
    selectedTripData,
    setIsMobileSortDialogOpen,
    shouldShowSelectedTrip,
    toggleFiltersPanel,
    setIsItineraryDrawerSelfTransferModalOpen,
  } = useResultPageContext();
  const { p } = useProperty();
  const { t } = useTranslation();
  const { currentSearchData, setSearchData } = useSearch();
  const flightResultsWrapperRef = useRef(null);
  const quickSortVersion = p('Result.Page.QuickSort.Version');
  const hasSelectedTrip = shouldShowSelectedTrip && Boolean(selectedTripData);
  const { selectedSortType } = currentSearchData || {};
  const sortingDropdownOptions = getSortingDropDownOptions(t, searchData?.type);
  const sortOptions = getQuickSortOptions(t);
  const quickSortOptions = [...sortOptions, ...sortingDropdownOptions];
  const selectedSortFieldLabel = quickSortOptions.find(
    (sortOption) => sortOption.value === selectedSortType,
  );

  return (
    <>
      {/* filtersV1 without any quickSort */}
      {quickSortVersion === 1 && (
        <Inline className={filters1MobileStyles}>
          {searchData && (
            <Wrapper>
              <ResultSearchFiltersV1
                availableSortTypes={searchData.availableSortTypes}
                debugFilters={searchData.availableFilters}
                filteredFlightsCount={searchData.filteredFlightsCount}
                flightsCount={searchData.flightsCount}
                handleClose={closeFiltersPanel}
                handleToggle={toggleFiltersPanel}
                isOpen={isFiltersPanelOpen}
                isSearchLoading={isSearchLoading}
                resultSetMetaData={searchData.resultSetMetaData}
                searchRoutes={searchData.routes}
              />
            </Wrapper>
          )}
        </Inline>
      )}

      {/* filters and quickSortV2 buttons */}
      {quickSortVersion === 2 && (
        <Inline className={sortAndFilterControlsStyles} noWrap spacing={8}>
          <ResultFiltersToggleButton />
          <Button
            className={sortingButtonStyles}
            data-testid="sorting-button"
            iconPrefix={<SortAscending aria-hidden />}
            onClick={() => setIsMobileSortDialogOpen(!isMobileSortDialogOpen)}
            size="small"
          >
            <span>{selectedSortFieldLabel?.labelKey ?? 'Sort'}</span>
          </Button>
        </Inline>
      )}

      <ContentWrapper>
        <Stack className={innerWrapperStyles(hasSelectedTrip)}>
          <div>
            {/* filters at drawer */}
            <div data-testid="resultPage-searchFilters" ref={sidebarRef}>
              <MobileFilters
                onClear={() =>
                  setSearchData!({ filters: { ...resetAllFilters(currentSearchData?.filters) } })
                }
                onFilter={onFilter}
                selectedFilters={selectedFilters}
              />
            </div>
            <div data-testid="resultPage-searchResults">
              <ResultsSection
                flightResultsWrapperRef={flightResultsWrapperRef}
                isNearbyDatesLoading={isNearbyDatesLoading}
                nearbyDatePrices={nearbyDatePrices}
                searchFormWatchRef={searchFormWatchRef}
                setFetchMoreResults={setFetchMoreResults}
                setValueRef={setValueRef}
              />
            </div>
          </div>
        </Stack>
      </ContentWrapper>

      <FloatingSaveTripButton
        setIsItineraryDrawerSelfTransferModalOpen={setIsItineraryDrawerSelfTransferModalOpen}
      />
    </>
  );
};

export default SearchResultsMobile;
